import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {PageHero, PageShell, PageTitle, Prose, Section} from 'components';
import {ROUTES} from 'constants/routes';
import {btnPrimaryLg} from 'components/v3/classes';

import {
  LanItem,
  engines,
  imageSourceLan,
  imageTargetLan,
  useLocalizedLan,
  videoSourceLan,
  videoTargetLan,
} from './constants';

/** 语言页（v3）：引擎卡 + 四张语言清单（图片翻译源/目标、视频翻译源/目标）。
 *  清单数据来自业务方 docx（2026-09，见 constants.ts 头注释），纯语种+代码两列；
 *  文本引擎矩阵已随旧数据结构移除（引擎介绍保留在上方引擎卡区）。 */

const Languages: React.FC = () => {
  const {t} = useTranslation('languages');
  const localizedImageTarget = useLocalizedLan(imageTargetLan);
  const localizedImageSource = useLocalizedLan(imageSourceLan);
  const localizedVideoTarget = useLocalizedLan(videoTargetLan);
  const localizedVideoSource = useLocalizedLan(videoSourceLan);

  /** hero 右侧语种标签云：目标语言抽样 + 数量徽章（静态 chip-solid，无动效成本） */
  const chipNames = imageTargetLan.filter((_, index) => index % 4 === 0).map((lan) => lan.name.replace(/\(.*\)/, ''));

  /** 单张清单表：语种 + 代码两列 */
  const renderLanTable = (list: LanItem[]): ReactElement => (
    <div className="v3-table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('table.language')}</th>
            <th>{t('table.code')}</th>
          </tr>
        </thead>
        <tbody>
          {list.map((lan) => (
            <tr key={lan.code}>
              <td>{lan.name}</td>
              <td>{lan.code}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  /** 语言清单：长列表（>20 条）在 lg 下拆两栏并排，缩短页面高度 */
  const renderLanList = (list: LanItem[]): ReactElement => {
    if (list.length <= 20) return renderLanTable(list);
    const half = Math.ceil(list.length / 2);
    return (
      <div className="grid gap-8 lg:grid-cols-2">
        {renderLanTable(list.slice(0, half))}
        {renderLanTable(list.slice(half))}
      </div>
    );
  };

  return (
    <PageShell>
      <PageTitle canonicalUrl="https://www.xiangjifanyi.com/home/language" title={t('meta.title')} />
      <PageHero
        actions={
          <Link className={btnPrimaryLg} to={ROUTES.imageTranslator}>
            {t('heroCta')}
          </Link>
        }
        aside={
          <div className="glass-card rounded-3xl p-7 lg:p-8 max-w-xl w-full mx-auto">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
              <span className="btn-primary rounded-full px-3 py-2 text-center text-sm font-medium text-white">
                {imageTargetLan.length}+
              </span>
              {chipNames.map((name) => (
                <span className="chip-solid rounded-full px-3 py-2 text-center text-sm text-slate-600" key={name}>
                  {name}
                </span>
              ))}
            </div>
          </div>
        }
        badge={t('hero.brand')}
        description={t('heroDesc')}
        title={t('hero.title')}
        variant="light"
      />
      <Section title={t('textEngines.title')}>
        <Prose>
          <p>
            {t('textEngines.lead')}
            <span className="text-indigo-600 font-medium">{t('textEngines.papago')}</span>
            {t('textEngines.googlePrefix')}
            <span className="text-indigo-600 font-medium">{t('textEngines.google')}</span>
            {t('textEngines.aliPrefix')}
            <span className="text-indigo-600 font-medium">{t('textEngines.ali')}</span>
            {t('textEngines.deeplPrefix')}
            <span className="text-indigo-600 font-medium">{t('textEngines.deepl')}</span>
            {t('textEngines.tail')} <Link to={ROUTES.contact}>{t('textEngines.contactLink')}</Link>
            {t('textEngines.ending')}
          </p>
        </Prose>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mt-10">
          {engines.map((engine) => (
            <a
              className="glass-card rounded-2xl p-5 flex flex-col items-center gap-3 transition-transform duration-300 hover:-translate-y-1"
              href={engine.url}
              key={engine.key}
              rel="noreferrer"
              target="_blank"
            >
              <img
                alt={engine.name}
                className="h-12 w-12 rounded-lg object-contain"
                decoding="async"
                src={engine.thumbnail}
              />
              <span className="text-sm font-medium text-slate-600">{engine.name}</span>
            </a>
          ))}
        </div>
      </Section>
      <Section bg="muted" title={t('imageSource.title')}>
        <Prose>{renderLanList(localizedImageSource)}</Prose>
      </Section>
      <Section title={t('imageTarget.title')}>
        <Prose>{renderLanList(localizedImageTarget)}</Prose>
      </Section>
      <Section bg="muted" title={t('videoSource.title')}>
        <Prose>{renderLanList(localizedVideoSource)}</Prose>
      </Section>
      <Section title={t('videoTarget.title')}>
        <Prose>{renderLanList(localizedVideoTarget)}</Prose>
      </Section>
    </PageShell>
  );
};

export default Languages;
